import React from 'react';
import type { NPC, ShopItem } from '../../../../shared/NPCData';
import type { CharacterClass } from '../../../../shared/CharacterTypes';
import { ITEM_DATABASE } from '../../../../shared/ItemData';
import { itemKindLabel } from '../../../../shared/equipmentSlots';
import { TomeButton, TomeSkin } from '../../primitives/tome';
import { ItemIcon } from '../../components/ItemIcon';
import { Tooltip, createItemTooltip } from '../../components/Tooltip';
import { PLAYER_UI, RARITY } from '../../theme';
import { MAX_SHOP_QUANTITY, quoteShopPurchase } from '../../npc/shop/purchaseQuote';
import './NPCPlayerUI.css';

interface ShopViewProps {
  npc: NPC;
  onShopPurchase?: (item: ShopItem, quantity: number) => void;
  playerGold?: number;
  viewerLevel?: number;
  viewerClass?: CharacterClass;
  pendingItemId?: string | null;
  purchaseRefusal?: { itemId: string; message: string } | null;
}
const ITEM_RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'unique'] as const;

function ShopCard({ item, onShopPurchase, playerGold = 0, viewerLevel, viewerClass,
  pendingItemId, purchaseRefusal }: Omit<ShopViewProps, 'npc'> & { item: ShopItem }) {
  const descriptionId = React.useId();
  const [quantityInput, setQuantityInput] = React.useState('1');
  const quantity = Number(quantityInput);
  const quote = quoteShopPurchase(item, quantity, playerGold);
  const canonical = ITEM_DATABASE[item.id];
  const rarity = canonical?.rarity ?? item.rarity;
  const itemType = canonical?.type ?? item.type;
  const tooltip = createItemTooltip({
    id: item.id, name: item.name, description: item.description, type: itemType, stats: item.stats,
    rarity: ITEM_RARITIES.find(known => known === rarity),
  }, { level: viewerLevel, characterClass: viewerClass });
  // Inventory activation/drag hints do not apply to goods the player has not bought.
  tooltip.interactionHint = 'Choose a quantity and review the total before buying.';
  const pending = pendingItemId === item.id;
  const disabled = !onShopPurchase || !quote.canPurchase || Boolean(pendingItemId);
  const currency = quote.goldCurrency ? 'Gold' : 'Gems';
  const totalPrice = quote.total === null ? 'Total unavailable' : `${quote.total.toLocaleString()} ${currency}`;
  const buyLabel = quantity === 1 ? 'Buy one' : quote.quantityValid ? `Buy ${quantity}` : 'Buy';
  const rarityColor = (RARITY as Record<string, string>)[rarity] ?? PLAYER_UI.text;

  return <Tooltip data={tooltip} position="right">
    <article className="npc-shop-card" aria-label={`${item.name} shop item`} tabIndex={0}
      data-rarity={rarity} style={{ '--npc-item-tone': rarityColor } as React.CSSProperties}>
      <TomeSkin layer="entry" texture="plain" shapeSeed={item.id} />
      <div className="npc-shop-item-header">
        <span className="npc-shop-item-art" aria-hidden="true" data-item-art-id={item.id}>
          <ItemIcon itemId={item.id} size={56} />
        </span>
        <div className="npc-shop-item-title"><h4>{item.name}</h4>
          <span>{[rarity, itemKindLabel(itemType, canonical?.equipmentSlot)?.replace(/_/g, ' ')].filter(Boolean).join(' · ')}</span>
        </div>
      </div>
      {(tooltip.description || item.description) && <p className="npc-shop-description">{tooltip.description || item.description}</p>}
      {!!tooltip.effects?.length && <ul className="npc-shop-facts">
        {tooltip.effects.map((effect, index) => <li key={index}>{effect}</li>)}
      </ul>}
      {!!tooltip.stats?.length && <dl className="npc-shop-stats">
        {tooltip.stats.map(stat => <div key={stat.label}><dt>{stat.label}</dt>
          <dd>{typeof stat.value === 'number' && stat.value > 0 ? '+' : ''}{stat.value}</dd></div>)}
      </dl>}
      {!!tooltip.requirements?.length && <div className="npc-shop-requirements" aria-label="Use and equip requirements">
        <span className="npc-card-kicker">To use or equip</span>
        {tooltip.requirements.map(requirement => <span key={requirement.text}
          data-item-requirement-status={requirement.status}>
          {requirement.text.replace(/^Requires /, '')}
        </span>)}
      </div>}
      <div className="npc-shop-purchase">
        <div id={descriptionId} className="npc-shop-price-block">
          <span>Total</span><strong className="npc-quoted-price">{totalPrice}</strong>
          {quote.priceValid && <span>{item.price.toLocaleString()} {currency} each</span>}
          {quote.stockKnown && <span>Stock: {item.stock!.toLocaleString()}</span>}
          {quote.reason && <span className="npc-action-unavailable">{quote.reason}</span>}
        </div>
        <label className="npc-shop-quantity">Quantity
          <input type="number" inputMode="numeric" min={1} max={MAX_SHOP_QUANTITY} step={1}
            aria-label={`Quantity for ${item.name}`} aria-describedby={descriptionId} aria-invalid={!quote.quantityValid}
            value={quantityInput} disabled={Boolean(pendingItemId)}
            onChange={event => setQuantityInput(event.currentTarget.value)} />
        </label>
        <TomeButton tone="primary" disabled={disabled} aria-label={`${pending ? 'Buying' : buyLabel} ${item.name}`} aria-describedby={descriptionId}
          onClick={event => { event.stopPropagation(); if (!disabled) onShopPurchase?.(item, quantity); }}>
          {pending ? 'Buying…' : buyLabel}
        </TomeButton>
      </div>
      {purchaseRefusal?.itemId === item.id && <p className="npc-purchase-refusal" role="alert">{purchaseRefusal.message}</p>}
    </article>
  </Tooltip>;
}

/** Read-only catalogue projection; costs and stock are quoted, never updated optimistically. */
export const ShopView: React.FC<ShopViewProps> = ({ npc, playerGold = 0, ...props }) => (
  <div className="npc-shop">
    <div className="npc-shop-summary"><h3>Goods & supplies</h3><span>Your Gold <strong>{playerGold.toLocaleString()}</strong></span></div>
    {npc.shopItems.length === 0 ? <p className="npc-empty" role="status">There are no goods available here.</p>
      : <div className="npc-shop-grid">{npc.shopItems.map(item => <ShopCard key={`${npc.id}:${item.id}`} item={item} playerGold={playerGold} {...props} />)}</div>}
  </div>
);
